import { useEffect, useState } from 'react';
import { type SettingInfoDto } from '../../../shared/api/pipeex/pcfSettingsClient';
import { useRevitParametersStore } from '../../../shared/stores/useRevitParametersStore';
import { loadProfileSettings } from '../lib/project-gate/profileSettingsInspection';
import {
  buildProfileContents,
  type ProfileContentsSummary,
} from '../lib/profileContents';

export interface ProfileContentsState {
  contents: ProfileContentsSummary | null;
  isLoading: boolean;
  error: string | null;
}

const emptyProfileContentsState: ProfileContentsState = {
  contents: null,
  isLoading: false,
  error: null,
};

/**
 * Loads the selected profile's settings DTO through the shared inspection
 * cache (already warm from match scoring in the common case) and classifies
 * its parameter references against the live project parameter catalog.
 */
export function useProfileContents(profile: SettingInfoDto | null): ProfileContentsState {
  const catalog = useRevitParametersStore((state) => state.parameters);
  const [state, setState] = useState<ProfileContentsState>(emptyProfileContentsState);

  useEffect(() => {
    if (!profile) {
      setState(emptyProfileContentsState);
      return;
    }

    let isActive = true;
    setState({ contents: null, isLoading: true, error: null });

    void loadProfileSettings(profile).then((result) => {
      if (!isActive) {
        return;
      }

      if (!result.dto) {
        setState({
          contents: null,
          isLoading: false,
          error: result.error ?? 'Profile contents could not be loaded.',
        });
        return;
      }

      setState({
        contents: buildProfileContents(result.dto, catalog),
        isLoading: false,
        error: null,
      });
    });

    return () => {
      isActive = false;
    };
  }, [profile, catalog]);

  return state;
}
